import { formMessages } from '@/constants';
import { formatFileSize } from '@/utils/textFormat';
import { useAppConfigQuery } from '@/hooks/useAppConfigQuery';
import type { FileRefType } from '@/types/file';
import { fileExtension } from './fileExtension';

export { fileExtension };

/**
 * Client-side upload validation (file-handling §14.4 Q6, §14.5 Phase 2).
 *
 * Rule (b): a would-be failure has to be visible while the person is still
 * sitting there. Extension and size are the two things we can know before a
 * single byte leaves the browser, and both are enforced server-side — the
 * presign passes the same per-type cap as an S3 content-length-range
 * condition. This module mirrors the server so the rejection is instant and
 * explains itself instead of arriving as an opaque S3 403.
 */

export type UploadRejectionReason = 'extension' | 'size';

export type UploadValidationResult =
  | { ok: true }
  | { ok: false; reason: UploadRejectionReason; message: string };

export type UploadMaxBytes = Record<FileRefType, number>;

/**
 * The server's published extension vocabulary (`GET /api/config/` →
 * `uploadExtensions`, vingapi `UPLOAD_EXTENSIONS`): every extension it will
 * accept, with its classification and byte cap. Keys are lowercase, no dot.
 */
export type UploadExtensions = Readonly<Record<string, { type: FileRefType; maxBytes: number }>>;

/**
 * Fallback caps, mirroring vingapi `src/shared/files/definitions.py`
 * `MAX_UPLOAD_BYTES`. **The server is the source of truth** — these apply only
 * before `GET /api/config/` has resolved (or for anonymous consume sessions
 * that never load it). Keep in sync if the server values change.
 */
export const FALLBACK_UPLOAD_MAX_BYTES: UploadMaxBytes = {
  image: 25 * 1024 * 1024, // 25 MB
  document: 100 * 1024 * 1024, // 100 MB
  audio: 500 * 1024 * 1024, // 500 MB
  video: 2 * 1024 * 1024 * 1024, // 2 GB
  other: 100 * 1024 * 1024, // 100 MB
};

/**
 * Extension → type, mirroring vingapi `EXTENSIONS`. Order matters and matches
 * the server's `get_file_type_from_extension`: `mp4` is listed under both
 * video and audio there, and image→video→audio→document iteration makes it
 * video. Anything unlisted is `other`.
 */
const EXTENSIONS: readonly (readonly [FileRefType, readonly string[]])[] = [
  ['image', ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'svg', 'heif', 'heic']],
  ['video', ['mp4', 'mkv', 'flv', 'mov', 'avi', 'wmv', 'hevc']],
  ['audio', ['mp3', 'aac', 'wav', 'ogg', 'flac', 'm4a']],
  ['document', ['pdf']],
] as const;


/**
 * Classify a filename the way the server does. Uses the server-published map
 * when given, else the mirrored table. Unknown extensions are `other`.
 */
export function fileTypeForFilename(filename: string, uploadExtensions?: UploadExtensions): FileRefType {
  const ext = fileExtension(filename);
  if (uploadExtensions) return uploadExtensions[ext]?.type ?? 'other';
  for (const [type, extensions] of EXTENSIONS) {
    if (extensions.includes(ext)) return type;
  }
  return 'other';
}

/**
 * The extensions the server accepts for these types, from the published map
 * when given, else the mirrored table. This is what a per-type upload button's
 * hard allow-list derives from — never a local guess the accept step would
 * then decline as `type_not_allowed` (plan A3 row 1).
 */
export function extensionsForTypes(
  types: readonly FileRefType[],
  uploadExtensions?: UploadExtensions,
): string[] {
  const wanted = new Set(types);
  if (uploadExtensions) {
    return Object.entries(uploadExtensions)
      .filter(([, entry]) => wanted.has(entry.type))
      .map(([ext]) => ext);
  }
  const out: string[] = [];
  for (const [type, extensions] of EXTENSIONS) {
    if (!wanted.has(type)) continue;
    for (const ext of extensions) if (!out.includes(ext)) out.push(ext);
  }
  return out;
}

export interface ValidateUploadOptions {
  /**
   * Extensions this surface accepts (with or without a leading dot, any case).
   * Omitted means "any extension the server allows".
   */
  allowedExtensions?: string[];
  /**
   * File types this surface accepts (e.g. a question's allow_* flags). The
   * accepted extensions are those types' extensions per `uploadExtensions`
   * (or the mirrored table); anything else — including an extension the
   * server does not know at all — is rejected by name.
   */
  allowedTypes?: readonly FileRefType[];
  /**
   * Per-type byte caps from `GET /api/config/` (`uploadMaxBytes`). Omitted
   * falls back to {@link FALLBACK_UPLOAD_MAX_BYTES}; prefer
   * {@link useUploadValidation}, which supplies the server values.
   */
  maxBytes?: Partial<UploadMaxBytes>;
  /**
   * The server's published extension → {type, maxBytes} map (`GET /api/config/`
   * `uploadExtensions`). When present it is the classification AND the cap;
   * `maxBytes` / the fallback table apply only without it.
   */
  uploadExtensions?: UploadExtensions;
}

function normalizeExtension(ext: string): string {
  return ext.replace(/^\./, '').toLowerCase();
}

/**
 * Validate a picked file before uploading it. Pure — no network, no React.
 */
export function validateUpload(
  file: File,
  opts: ValidateUploadOptions = {},
): UploadValidationResult {
  const ext = fileExtension(file.name);

  // The hard allow-list: an explicit extension list, else the extensions of
  // the allowed types. Either way the rejection names what IS accepted.
  const allowed =
    opts.allowedExtensions && opts.allowedExtensions.length > 0
      ? opts.allowedExtensions.map(normalizeExtension)
      : opts.allowedTypes
        ? extensionsForTypes(opts.allowedTypes, opts.uploadExtensions)
        : null;
  if (allowed && (!ext || !allowed.includes(ext))) {
    return {
      ok: false,
      reason: 'extension',
      message: formMessages.upload.extensionRejected(
        file.name,
        allowed.map((e) => `.${e}`).join(', '),
      ),
    };
  }

  const type = fileTypeForFilename(file.name, opts.uploadExtensions);
  const cap =
    opts.uploadExtensions?.[ext]?.maxBytes ??
    opts.maxBytes?.[type] ??
    FALLBACK_UPLOAD_MAX_BYTES[type];
  if (file.size > cap) {
    return {
      ok: false,
      reason: 'size',
      message: formMessages.upload.tooLarge(
        formatFileSize(file.size),
        formMessages.upload.typeLabels[type]!,
        formatFileSize(cap),
      ),
    };
  }

  return { ok: true };
}

/**
 * The server-published upload vocabulary, from the same `GET /api/config/`
 * query the rest of the app uses. Both fields are undefined until it resolves
 * (and for anonymous consume sessions, which never load it) — the mirrored
 * fallbacks then apply.
 */
export function useUploadConfig(): {
  maxBytes: Partial<UploadMaxBytes> | undefined;
  uploadExtensions: UploadExtensions | undefined;
} {
  const { data: appConfig } = useAppConfigQuery();
  return { maxBytes: appConfig?.uploadMaxBytes, uploadExtensions: appConfig?.uploadExtensions };
}

/**
 * `validateUpload` bound to the server's published caps and extensions.
 *
 * Reads `uploadMaxBytes` / `uploadExtensions` from the same `GET /api/config/`
 * query the rest of the app uses; before it resolves the typed fallback above
 * applies.
 */
export function useUploadValidation(): (
  file: File,
  opts?: Omit<ValidateUploadOptions, 'maxBytes' | 'uploadExtensions'>,
) => UploadValidationResult {
  const { maxBytes, uploadExtensions } = useUploadConfig();
  return (file, opts = {}) => validateUpload(file, { ...opts, maxBytes, uploadExtensions });
}
